import "./style.scss";

import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import InlineButton from "components/shared/InlineButton";
import type { IWork, IWorkTitleSummary } from "containers/Works/types";
import type { HTMLAttributes } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ITermFormData } from "..";
import CoveredTitlesChip from "./CoveredTitlesChip";
import CoveredTitlesOption from "./CoveredTitlesOption";
import CoveredTitlesStack from "./CoveredTitlesStack";
import { filterTitlesByQuery, titleSearchText, titlesForPicker, titlesForStack } from "./titleDisplay";

type CoveredTitlesFieldProps = {
    useFormData: Pick<ITermFormData, "control" | "errors" | "watch" | "setValue">;
    work: IWork | null;
    workTitles: IWorkTitleSummary[];
    loading?: boolean;
    startsAsException?: boolean;
};

const CoveredTitlesField = ({
    useFormData,
    work,
    workTitles,
    loading = false,
    startsAsException = false,
}: CoveredTitlesFieldProps) => {
    const [t] = useTranslation();
    const { control, errors, watch, setValue } = useFormData;
    const selectedTitles = watch("coveredTitles") ?? [];
    const isException = selectedTitles.length > 0;
    const [isPicking, setIsPicking] = useState(startsAsException);

    useEffect(() => {
        if (isException) setIsPicking(true);
    }, [isException]);

    const showPicker = isPicking || isException;
    const stackTitles = titlesForStack(selectedTitles, workTitles);
    const pickerOptions = titlesForPicker(selectedTitles, workTitles);

    const applyToEveryArticle = () => {
        setValue?.("coveredTitles", [], { shouldDirty: true });
        setIsPicking(false);
    };

    const removeTitle = (titleId: number) => {
        const remaining = selectedTitles.filter((title) => title.id !== titleId);
        setValue?.("coveredTitles", remaining, { shouldDirty: true });
        if (remaining.length === 0) setIsPicking(false);
    };

    return (
        <div className="form-component covered-titles-field">
            <label className="form-component-label" htmlFor="coveredTitles">
                {t("ConnectTitle.CoveredTitles")}
            </label>
            <div className="covered-titles-box">
                <div
                    className={`covered-titles-stack-slot${showPicker ? " covered-titles-stack-slot--hidden" : ""}`}
                    aria-hidden={showPicker}
                >
                    <CoveredTitlesStack titles={stackTitles} workName={work?.name ?? ""} />
                </div>
                <div className="covered-titles-content">
                    {showPicker ? (
                        <>
                            <span className="covered-titles-title">
                                {t("ConnectTitle.CoveredTitlesExceptionTitle")}
                            </span>
                            <p className="covered-titles-hint">{t("ConnectTitle.CoveredTitlesExceptionHint")}</p>
                            <AutocompleteFormComponent
                                multiple
                                filterSelectedOptions
                                options={pickerOptions}
                                control={control}
                                controlName="coveredTitles"
                                error={errors.coveredTitles}
                                disabled={!work}
                                loading={loading}
                                placeholder={t("ConnectTitle.CoveredTitlesPickerPlaceholder")}
                                filterOptions={(options, state) =>
                                    filterTitlesByQuery(options as IWorkTitleSummary[], state.inputValue)
                                }
                                renderTags={() => null}
                                renderOptionText={titleSearchText}
                                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IWorkTitleSummary) => (
                                    <CoveredTitlesOption option={option} listProps={props} />
                                )}
                                isOptionEqualToValue={(option: IWorkTitleSummary, value: IWorkTitleSummary) =>
                                    option.id === value.id
                                }
                                onChange={(_, data) => {
                                    const selected = Array.isArray(data) ? data : [];
                                    if (workTitles.length > 0 && selected.length === workTitles.length) {
                                        applyToEveryArticle();
                                    }
                                }}
                            />
                            <InlineButton
                                size="small"
                                icon={<LayersOutlinedIcon sx={{ width: 16, height: 16 }} />}
                                onClick={applyToEveryArticle}
                            >
                                {t("ConnectTitle.CoveredTitlesApplyAll")}
                            </InlineButton>
                            {isException && (
                                <div className="covered-titles-chips">
                                    {selectedTitles.map((title) => (
                                        <CoveredTitlesChip key={title.id} title={title} onRemove={removeTitle} />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <span className="covered-titles-title">{t("ConnectTitle.CoveredTitlesDefaultTitle")}</span>
                            <p className="covered-titles-body">{t("ConnectTitle.CoveredTitlesDefaultBody")}</p>
                            <InlineButton
                                size="small"
                                icon={<ChecklistOutlinedIcon sx={{ width: 16, height: 16 }} />}
                                onClick={() => setIsPicking(true)}
                                disabled={!work || loading}
                            >
                                {t("ConnectTitle.CoveredTitlesSelectException")}
                            </InlineButton>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoveredTitlesField;
